import React, { useMemo, useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Paper,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TablePagination,
  Button,
  Divider,
  Chip,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useGetSitesQuery } from "../../../features/sites/sitesApiSlice";

const normalizeValue = (value) => String(value || "").trim();
const normalizeKey = (value) => normalizeValue(value).toLowerCase();

function SiteMappingStep({ cleanedData, parsedData, onMapped }) {
  const { data: sites = [], isLoading: sitesLoading } = useGetSitesQuery();
  const [mode, setMode] = useState("auto"); // auto | single | field
  const [mappingField, setMappingField] = useState("");
  const [singleAssignment, setSingleAssignment] = useState({
    type: "existing",
    siteId: "",
    siteName: "",
  });
  const [valueMappings, setValueMappings] = useState({});
  const [fallbackAssignment, setFallbackAssignment] = useState({
    type: "existing",
    siteId: "",
    siteName: "",
  });
  const [previewPage, setPreviewPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const hasData = Array.isArray(cleanedData) && cleanedData.length > 0;

  const siteById = useMemo(() => {
    const map = new Map();
    sites.forEach((site) => {
      const id = site?._id || site?.id;
      if (id) map.set(String(id), site);
    });
    return map;
  }, [sites]);

  const siteByName = useMemo(() => {
    const map = new Map();
    sites.forEach((site) => {
      const name = normalizeValue(site?.siteName);
      if (name) map.set(normalizeKey(name), site);
    });
    return map;
  }, [sites]);

  const availableFields = useMemo(() => {
    if (!hasData) return [];
    const keys = new Set();
    cleanedData.forEach((row) => {
      Object.keys(row || {}).forEach((key) => {
        if (!key || key.startsWith("_") || key === "sourceIndex") return;
        keys.add(key);
      });
    });

    const fields = Array.from(keys)
      .sort()
      .map((key) => ({ value: key, label: key, source: "cleaned" }));

    if (parsedData?.headers && Array.isArray(parsedData.headers)) {
      parsedData.headers.forEach((header) => {
        fields.push({
          value: `raw:${header}`,
          label: `${header} (Original)`,
          source: "raw",
        });
      });
    }

    return fields;
  }, [cleanedData, hasData, parsedData]);

  useEffect(() => {
    if (!availableFields.length) return;
    if (mappingField) return;

    const preferred = ["site", "siteName", "standort", "standortName"];
    const match = availableFields.find((field) =>
      preferred.includes(field.value),
    );
    setMappingField(match ? match.value : availableFields[0]?.value || "");
  }, [availableFields, mappingField]);

  const getRawFieldValue = useCallback(
    (row, fieldKey) => {
      if (!fieldKey.startsWith("raw:")) return undefined;
      const header = fieldKey.replace("raw:", "");
      const index = row?.sourceIndex;
      if (
        typeof index !== "number" ||
        !parsedData?.rows ||
        !parsedData.rows[index]
      ) {
        return undefined;
      }
      return parsedData.rows[index][header];
    },
    [parsedData],
  );

  const getFieldValue = useCallback(
    (row, fieldKey) => {
      if (!fieldKey) return undefined;
      if (fieldKey.startsWith("raw:")) {
        return getRawFieldValue(row, fieldKey);
      }
      return row ? row[fieldKey] : undefined;
    },
    [getRawFieldValue],
  );

  const uniqueFieldValues = useMemo(() => {
    if (!hasData || mode !== "field" || !mappingField) return [];
    const map = new Map();
    cleanedData.forEach((row) => {
      const rawValue = getFieldValue(row, mappingField);
      const value = normalizeValue(rawValue);
      const key = normalizeKey(value);
      if (!key) return;
      if (!map.has(key)) {
        map.set(key, { value, count: 1 });
      } else {
        map.get(key).count += 1;
      }
    });
    return Array.from(map.values()).sort((a, b) =>
      a.value.localeCompare(b.value),
    );
  }, [cleanedData, getFieldValue, hasData, mappingField, mode]);

  useEffect(() => {
    if (!uniqueFieldValues.length) return;
    setValueMappings((prev) => {
      const next = { ...prev };
      uniqueFieldValues.forEach((item) => {
        const key = normalizeKey(item.value);
        if (next[key]) return;
        const existingSite = siteByName.get(key);
        if (existingSite) {
          next[key] = { type: "existing", siteId: existingSite._id };
        } else {
          next[key] = { type: "new", siteName: item.value };
        }
      });
      return next;
    });
  }, [siteByName, uniqueFieldValues]);

  const getAssignmentLabel = useCallback(
    (assignment) => {
      if (!assignment) return "";
      if (assignment.type === "existing" && assignment.siteId) {
        return siteById.get(String(assignment.siteId))?.siteName || "";
      }
      if (assignment.type === "new") {
        return assignment.siteName || "";
      }
      return "";
    },
    [siteById],
  );

  const resolveAutoSite = useCallback(
    (row) => {
      const memberNumber = normalizeValue(row?.memberNumber).toUpperCase();
      if (!memberNumber) return { label: "Standardstandort", siteId: null };
      const match = sites.find((site) => {
        const prefix = normalizeValue(site?.memberIdentifier).toUpperCase();
        return prefix && memberNumber.startsWith(prefix);
      });
      if (match) {
        return { label: match.siteName || "", siteId: match._id || match.id };
      }
      return { label: "Standardstandort", siteId: null };
    },
    [sites],
  );

  const resolveAssignmentForRow = useCallback(
    (row) => {
      if (mode === "single") {
        return singleAssignment;
      }

      if (mode === "field") {
        const rawValue = getFieldValue(row, mappingField);
        const value = normalizeValue(rawValue);
        const key = normalizeKey(value);
        if (key && valueMappings[key]) {
          return valueMappings[key];
        }
        return fallbackAssignment;
      }

      return null;
    },
    [
      fallbackAssignment,
      getFieldValue,
      mappingField,
      mode,
      singleAssignment,
      valueMappings,
    ],
  );

  const previewRows = useMemo(() => {
    if (!hasData) return [];
    return cleanedData.map((row) => {
      if (mode === "auto") {
        const auto = resolveAutoSite(row);
        return {
          row,
          siteLabel: auto.label,
          siteId: auto.siteId,
        };
      }
      const assignment = resolveAssignmentForRow(row);
      return {
        row,
        siteLabel: getAssignmentLabel(assignment) || "Nicht zugeordnet",
        assignment,
      };
    });
  }, [
    cleanedData,
    getAssignmentLabel,
    hasData,
    mode,
    resolveAssignmentForRow,
    resolveAutoSite,
  ]);

  const handleContinue = () => {
    if (!hasData) {
      onMapped([]);
      return;
    }

    const mappedData = cleanedData.map((row) => {
      const { importSiteId, importSiteName, ...rest } = row;

      if (mode === "auto") {
        return rest;
      }

      const assignment = resolveAssignmentForRow(row);
      if (!assignment || !getAssignmentLabel(assignment)) {
        return rest;
      }

      if (assignment.type === "existing" && assignment.siteId) {
        return { ...rest, importSiteId: assignment.siteId };
      }

      if (assignment.type === "new" && assignment.siteName) {
        return { ...rest, importSiteName: assignment.siteName };
      }

      return rest;
    });

    onMapped(mappedData);
  };

  const handleChangePreviewPage = (event, newPage) => {
    setPreviewPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPreviewPage(0);
  };

  if (!hasData) {
    return (
      <Box>
        <Typography
          variant="h5"
          gutterBottom
          fontWeight="bold"
          textAlign="center"
        >
          Standort-Zuordnung
        </Typography>
        <Alert severity="info">
          Keine bereinigten Daten vorhanden. Bitte gehen Sie einen Schritt
          zurück und prüfen Sie die Daten.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography
        variant="h5"
        gutterBottom
        fontWeight="bold"
        textAlign="center"
      >
        Standort-Zuordnung
      </Typography>
      <Typography
        variant="body1"
        color="text.secondary"
        textAlign="center"
        paragraph
      >
        Wählen Sie aus, wie Mitglieder Standorten zugeordnet werden sollen.
      </Typography>

      {sitesLoading && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Standorte werden geladen...
        </Alert>
      )}

      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom fontWeight="bold">
          Zuordnungsmodus
        </Typography>
        <RadioGroup
          row
          value={mode}
          onChange={(event) => setMode(event.target.value)}
        >
          <FormControlLabel
            value="auto"
            control={<Radio />}
            label="Automatisch (Mitgliedsnummer-Präfix)"
          />
          <FormControlLabel
            value="single"
            control={<Radio />}
            label="Alle Mitglieder in einen Standort"
          />
          <FormControlLabel
            value="field"
            control={<Radio />}
            label="Nach Feldwert zuordnen"
          />
        </RadioGroup>

        {mode === "single" && (
          <Box sx={{ mt: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
            <FormControl sx={{ minWidth: 240 }}>
              <InputLabel id="single-site-select-label">
                Bestehender Standort
              </InputLabel>
              <Select
                labelId="single-site-select-label"
                label="Bestehender Standort"
                value={singleAssignment.siteId}
                onChange={(event) =>
                  setSingleAssignment({
                    type: "existing",
                    siteId: event.target.value,
                    siteName: "",
                  })
                }
              >
                {sites.map((site) => (
                  <MenuItem key={site?._id || site?.id} value={site?._id || site?.id}>
                    {site.siteName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Neuen Standort erstellen"
              value={singleAssignment.type === "new" ? singleAssignment.siteName : ""}
              onChange={(event) =>
                setSingleAssignment({
                  type: "new",
                  siteId: "",
                  siteName: event.target.value,
                })
              }
              placeholder="z. B. Innenstadt"
              sx={{ minWidth: 240 }}
            />
          </Box>
        )}

        {mode === "field" && (
          <Box sx={{ mt: 2 }}>
            <FormControl sx={{ minWidth: 280, mb: 2 }}>
              <InputLabel id="mapping-field-select-label">
                Feld für Zuordnung
              </InputLabel>
              <Select
                labelId="mapping-field-select-label"
                label="Feld für Zuordnung"
                value={mappingField}
                onChange={(event) => setMappingField(event.target.value)}
              >
                {availableFields.map((field) => (
                  <MenuItem key={field.value} value={field.value}>
                    {field.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {uniqueFieldValues.length === 0 && (
              <Alert severity="warning">
                Für das ausgewählte Feld wurden keine Werte gefunden.
              </Alert>
            )}

            {uniqueFieldValues.length > 0 && (
              <Box>
                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                  Feldwerte zu Standorten
                </Typography>
                <TableContainer component={Paper} sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "primary.lighter" }}>
                        <TableCell>Feldwert</TableCell>
                        <TableCell>Anzahl Mitglieder</TableCell>
                        <TableCell>Zuordnung</TableCell>
                        <TableCell>Neuer Standortname</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {uniqueFieldValues.map((item) => {
                        const key = normalizeKey(item.value);
                        const assignment = valueMappings[key] || {};
                        return (
                          <TableRow key={key}>
                            <TableCell>{item.value}</TableCell>
                            <TableCell>{item.count}</TableCell>
                            <TableCell>
                              <Select
                                size="small"
                                value={
                                  assignment.type === "existing"
                                    ? assignment.siteId
                                    : "new"
                                }
                                onChange={(event) => {
                                  const value = event.target.value;
                                  setValueMappings((prev) => ({
                                    ...prev,
                                    [key]:
                                      value === "new"
                                        ? {
                                            type: "new",
                                            siteName: assignment.siteName || item.value,
                                          }
                                        : { type: "existing", siteId: value },
                                  }));
                                }}
                              >
                                <MenuItem value="new">Neu erstellen</MenuItem>
                                {sites.map((site) => (
                                  <MenuItem
                                    key={site?._id || site?.id}
                                    value={site?._id || site?.id}
                                  >
                                    {site.siteName}
                                  </MenuItem>
                                ))}
                              </Select>
                            </TableCell>
                            <TableCell>
                              {assignment.type === "new" && (
                                <TextField
                                  size="small"
                                  value={assignment.siteName || item.value}
                                  onChange={(event) =>
                                    setValueMappings((prev) => ({
                                      ...prev,
                                      [key]: {
                                        type: "new",
                                        siteName: event.target.value,
                                      },
                                    }))
                                  }
                                />
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Divider sx={{ mb: 2 }} />
                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                  Fallback-Standort
                </Typography>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <FormControl sx={{ minWidth: 240 }}>
                    <InputLabel id="fallback-site-select-label">
                      Bestehender Standort
                    </InputLabel>
                    <Select
                      labelId="fallback-site-select-label"
                      label="Bestehender Standort"
                      value={fallbackAssignment.siteId}
                      onChange={(event) =>
                        setFallbackAssignment({
                          type: "existing",
                          siteId: event.target.value,
                          siteName: "",
                        })
                      }
                    >
                      {sites.map((site) => (
                        <MenuItem key={site?._id || site?.id} value={site?._id || site?.id}>
                          {site.siteName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    label="Neuen Standort erstellen"
                    value={
                      fallbackAssignment.type === "new"
                        ? fallbackAssignment.siteName
                        : ""
                    }
                    onChange={(event) =>
                      setFallbackAssignment({
                        type: "new",
                        siteId: "",
                        siteName: event.target.value,
                      })
                    }
                    placeholder="z. B. Hauptstandort"
                    sx={{ minWidth: 240 }}
                  />
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Paper>

      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom fontWeight="bold">
          Vorschau der Zuordnung
        </Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
          {previewRows.slice(0, 8).map((item, idx) => (
            <Chip
              key={`${item.row?.memberNumber || idx}-${idx}`}
              label={`${item.row?.firstName || ""} ${item.row?.lastName || ""} → ${
                item.siteLabel
              }`}
              variant="outlined"
            />
          ))}
          {previewRows.length > 8 && (
            <Chip label={`+${previewRows.length - 8} weitere`} />
          )}
        </Box>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "primary.lighter" }}>
                <TableCell>Mitglied</TableCell>
                <TableCell>Mitgliedsnummer</TableCell>
                {mode === "field" && <TableCell>Feldwert</TableCell>}
                <TableCell>Standort</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {previewRows
                .slice(previewPage * rowsPerPage, previewPage * rowsPerPage + rowsPerPage)
                .map((item, index) => (
                  <TableRow key={`${item.row?.memberNumber || index}-${index}`}>
                    <TableCell>
                      {item.row?.firstName} {item.row?.lastName}
                    </TableCell>
                    <TableCell>{item.row?.memberNumber || "-"}</TableCell>
                    {mode === "field" && (
                      <TableCell>
                        {normalizeValue(getFieldValue(item.row, mappingField)) || "-"}
                      </TableCell>
                    )}
                    <TableCell>{item.siteLabel}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={previewRows.length}
            rowsPerPage={rowsPerPage}
            page={previewPage}
            onPageChange={handleChangePreviewPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      </Paper>

      <Box sx={{ mt: 4, textAlign: "center" }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleContinue}
          endIcon={<Icon icon="mdi:arrow-right" />}
        >
          Weiter zur Validierung
        </Button>
      </Box>
    </Box>
  );
}

SiteMappingStep.propTypes = {
  cleanedData: PropTypes.array.isRequired,
  parsedData: PropTypes.object,
  onMapped: PropTypes.func.isRequired,
};

SiteMappingStep.defaultProps = {
  parsedData: null,
};

export default SiteMappingStep;
